import { CanActivateFn, Route, UrlMatchResult, UrlSegment } from '@angular/router';
import { environment } from 'src/environments/environment';

/**
 * Rollout switch for the new onboarding app (fusion-onboarding) — environment.newOnboarding.
 *
 * Off: nothing changes — sign-in, sign-up and the old get-started flow stay on this app's own
 * /account/* and /get-started/* pages.
 * On: those URLs are taken over by the new app. Every place that sends a user to sign in
 * (logout, an expired session, AuthGuardService) already ends up on /account/signin, so routing
 * just that prefix away covers all of them — no logout code needs to know which flow is live.
 */
const LEGACY_ONBOARDING_PREFIXES = new Set(['account', 'get-started']);

/** Matches /account, /account/<anything>, /get-started, /get-started/<anything>. */
function legacyOnboardingMatcher(segments: UrlSegment[]): UrlMatchResult | null {
    return segments.length && LEGACY_ONBOARDING_PREFIXES.has(segments[0].path) ? { consumed: segments } : null;
}

/** Sends the user to the new onboarding app's /signup for /account/signup, /login for everything
 *  else, keeping ?returnUrl= (made absolute so the other app can bring them back here). */
const newOnboardingRedirectGuard: CanActivateFn = (_route, state) => {
    const [path, query = ''] = state.url.split('?');
    const page = path === '/account/signup' ? 'signup' : 'login';
    const target = new URL(page, environment.newOnboarding.appUrl);

    const returnUrl = new URLSearchParams(query).get('returnUrl');
    if (returnUrl) {
        target.searchParams.set('returnUrl', new URL(returnUrl, location.origin).toString());
    }

    window.location.assign(target.toString());
    return false;
};

/** Put first in the app routes: empty when the new onboarding isn't rolled out. */
export const NEW_ONBOARDING_REDIRECT_ROUTES: Route[] = environment.newOnboarding.enabled
    ? [{ matcher: legacyOnboardingMatcher, canActivate: [newOnboardingRedirectGuard], children: [] }]
    : [];
